/**
 * Hook for Plaid Link integration
 *
 * Manages the Plaid Link flow for connecting bank accounts.
 * Uses the official react-plaid-link package when available,
 * or falls back to dynamic script loading.
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';

export interface UsePlaidLinkOptions {
  /** Callback when user successfully links an account */
  onSuccess?: (publicToken: string, metadata: PlaidLinkMetadata) => void | Promise<void>;
  /** Callback when user exits without completing */
  onExit?: (error: PlaidLinkError | null, metadata: PlaidLinkMetadata) => void;
  /** Callback when Link loads */
  onLoad?: () => void;
  /** Callback when an event occurs */
  onEvent?: (eventName: string, metadata: PlaidLinkMetadata) => void;
  /** Redirect URI for OAuth flows (mobile/desktop apps) */
  redirectUri?: string;
}

export interface UsePlaidLinkResult {
  /** Opens the Plaid Link modal */
  open: () => void;
  /** Whether Plaid Link is ready to be opened */
  ready: boolean;
  /** Whether Plaid Link is currently open */
  isOpen: boolean;
  /** Whether Link is loading */
  isLoading: boolean;
  /** Error if Link failed to initialize */
  error: Error | null;
  /** Refresh the link token */
  refreshLinkToken: () => Promise<void>;
}

export interface PlaidLinkMetadata {
  institution: {
    name: string;
    institution_id: string;
  } | null;
  accounts: Array<{
    id: string;
    name: string;
    mask: string;
    type: string;
    subtype: string;
  }>;
  link_session_id: string;
}

export interface PlaidLinkError {
  error_type: string;
  error_code: string;
  error_message: string;
  display_message: string | null;
}

// Plaid Link script URL
const PLAID_LINK_SCRIPT_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

// Global to track if script is loaded
let plaidScriptLoaded = false;
let plaidScriptLoading = false;
const scriptLoadCallbacks: Array<() => void> = [];

/**
 * Loads the Plaid Link script dynamically
 */
async function loadPlaidScript(): Promise<void> {
  if (plaidScriptLoaded) {
    return;
  }

  if (plaidScriptLoading) {
    // Wait for existing load to complete
    return new Promise((resolve) => {
      scriptLoadCallbacks.push(resolve);
    });
  }

  plaidScriptLoading = true;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PLAID_LINK_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      plaidScriptLoaded = true;
      plaidScriptLoading = false;
      resolve();
      // Resolve any waiting callbacks
      scriptLoadCallbacks.forEach((cb) => cb());
      scriptLoadCallbacks.length = 0;
    };
    script.onerror = () => {
      plaidScriptLoading = false;
      reject(new Error('Failed to load Plaid Link script'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Hook for managing Plaid Link flow
 */
export function usePlaidLink(options: UsePlaidLinkOptions = {}): UsePlaidLinkResult {
  const { onSuccess, onExit, onLoad, onEvent, redirectUri } = options;

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [linkHandler, setLinkHandler] = useState<PlaidLinkHandler | null>(null);

  // Fetch link token on mount
  const fetchLinkToken = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if Plaid is configured
      const status = await api.getBankingStatus();
      if (!status.configured) {
        throw new Error('Banking integration is not configured');
      }

      // Get link token
      const response = await api.createBankingLinkToken({ redirectUri });
      setLinkToken(response.linkToken);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [redirectUri]);

  // Initialize Plaid Link
  useEffect(() => {
    let mounted = true;
    let handler: PlaidLinkHandler | null = null;

    async function init() {
      if (!linkToken) return;

      try {
        // Load Plaid script if needed
        await loadPlaidScript();

        if (!mounted) return;

        // Create Plaid Link handler
        // @ts-expect-error - Plaid is loaded dynamically
        handler = window.Plaid.create({
          token: linkToken,
          onSuccess: async (public_token: string, metadata: PlaidLinkMetadata) => {
            setIsOpen(false);
            if (onSuccess) {
              await onSuccess(public_token, metadata);
            }
          },
          onExit: (err: PlaidLinkError | null, metadata: PlaidLinkMetadata) => {
            setIsOpen(false);
            if (onExit) {
              onExit(err, metadata);
            }
          },
          onLoad: () => {
            if (onLoad) {
              onLoad();
            }
          },
          onEvent: (eventName: string, metadata: PlaidLinkMetadata) => {
            if (onEvent) {
              onEvent(eventName, metadata);
            }
          },
        });

        if (mounted) {
          setLinkHandler(handler);
          setReady(true);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    }

    init();

    return () => {
      mounted = false;
      if (handler) {
        handler.destroy();
      }
    };
  }, [linkToken, onSuccess, onExit, onLoad, onEvent]);

  // Fetch link token on mount
  useEffect(() => {
    fetchLinkToken();
  }, [fetchLinkToken]);

  // Open Plaid Link
  const open = useCallback(() => {
    if (linkHandler && ready) {
      setIsOpen(true);
      linkHandler.open();
    }
  }, [linkHandler, ready]);

  // Refresh link token
  const refreshLinkToken = useCallback(async () => {
    setReady(false);
    if (linkHandler) {
      linkHandler.destroy();
      setLinkHandler(null);
    }
    await fetchLinkToken();
  }, [fetchLinkToken, linkHandler]);

  return {
    open,
    ready,
    isOpen,
    isLoading,
    error,
    refreshLinkToken,
  };
}

// Type for the Plaid Link handler
interface PlaidLinkHandler {
  open: () => void;
  destroy: () => void;
}

// Declare Plaid on window for TypeScript
declare global {
  interface Window {
    Plaid?: {
      create: (config: {
        token: string;
        onSuccess: (publicToken: string, metadata: PlaidLinkMetadata) => void;
        onExit: (err: PlaidLinkError | null, metadata: PlaidLinkMetadata) => void;
        onLoad?: () => void;
        onEvent?: (eventName: string, metadata: PlaidLinkMetadata) => void;
      }) => PlaidLinkHandler;
    };
  }
}
